import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import {
  getMeasureContext,
  truncateByCharacterCount,
  truncateMiddleToWidth,
} from './middleTruncatedTextMeasure'

const TruncatedTextContainer = styled.span<{
  $maxWidth?: string
  $fillWidth?: boolean
}>`
  display: block;
  position: relative;
  max-width: ${({ $maxWidth }) => $maxWidth || '15rem'};
  ${({ $fillWidth }) => $fillWidth && 'width: 100%;'}
  overflow: hidden;
  white-space: nowrap;
`

export interface MiddleTruncatedTextProps {
  text: string
  maxLength?: number
  maxWidth?: string
  className?: string
  tooltipFontSize?: string
  fillWidth?: boolean
}

export const MiddleTruncatedText: React.FC<MiddleTruncatedTextProps> = ({
  text,
  maxLength = 30,
  maxWidth = '15rem',
  className,
  tooltipFontSize,
  fillWidth = false,
}) => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const fontRef = useRef<string | null>(null)
  const lastWidthRef = useRef<number | null>(null)
  const [displayText, setDisplayText] = useState(text)
  const [isTruncated, setIsTruncated] = useState(false)

  const calculateTruncation = useCallback(
    (knownWidth?: number) => {
      if (!fillWidth) {
        const truncated = truncateByCharacterCount(text, maxLength)
        setDisplayText(truncated.displayText)
        setIsTruncated(truncated.isTruncated)
        return
      }
      const container = containerRef.current
      const ctx = getMeasureContext()
      if (!container || !ctx) return
      const containerWidth = knownWidth ?? container.clientWidth
      if (containerWidth === 0) return
      lastWidthRef.current = containerWidth
      if (fontRef.current === null) {
        const style = window.getComputedStyle(container)
        fontRef.current = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
      }
      ctx.font = fontRef.current
      const truncated = truncateMiddleToWidth(
        text,
        containerWidth,
        (value) => ctx.measureText(value).width
      )
      setDisplayText(truncated.displayText)
      setIsTruncated(truncated.isTruncated)
    },
    [fillWidth, maxLength, text]
  )

  useLayoutEffect(() => {
    calculateTruncation()
    if (!fillWidth) return
    const container = containerRef.current
    if (!container) return
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width === undefined || width === lastWidthRef.current) return
      calculateTruncation(width)
    })
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [calculateTruncation, fillWidth])

  const truncatedContent = (
    <TruncatedTextContainer
      ref={containerRef}
      className={className}
      $maxWidth={maxWidth}
      $fillWidth={fillWidth}>
      {displayText}
    </TruncatedTextContainer>
  )

  if (!isTruncated) {
    return truncatedContent
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{truncatedContent}</TooltipTrigger>
      <TooltipContent
        style={tooltipFontSize ? { fontSize: tooltipFontSize } : undefined}>
        {text}
      </TooltipContent>
    </Tooltip>
  )
}
